import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import InstanceItemRow from './InstanceItemRow';
import { Instance } from '../../../app/services/api/orderManagementClient';
import { useTranslation } from 'react-i18next';
import { Pill } from '../../../components/shared/Pill';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { EmptyTable } from '../../../components/shared/EmptyTable';

type Props = {
  list: Instance[];
  isLoading: boolean;
  totalCount: number;
  onClear: () => void;
  onCreateNew: () => void;
};

const InstancesList: FC<Props> = ({
  list,
  isLoading,
  totalCount,
  onClear,
  onCreateNew,
}) => {
  const { t } = useTranslation('instances');

  if (totalCount === 0 && !isLoading) {
    return (
      <EmptyTable
        bodyRowHeight={40}
        headerHeight={45}
        text={t('instances')}
        searchTerm={''}
        onClear={onClear}
        onAdd={onCreateNew}
        onAddIsCreating={false}
        addItemText={t('createNew')}
        onAddIsDisabled={false}
        withPaginationShown={false}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          p: 5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {/* Left: Title and Pill */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="textMD"
            fontWeight={600}>
            {t('instances')}
          </Typography>
          {(totalCount ?? 0) > 0 && <Pill text={`${totalCount} ${t('instances')}`} />}
        </Box>
      </Box>
      
      {isLoading ? (
        <TableLoading
          headerHeight={45}
          bodyRowHeight={40}
          minTableWidth={1200}
        />
      ) : (
        <TableContainer>
          <Table sx={{ borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', minWidth: '1200px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {t('name')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  ID
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('system')}
                </TableCell>
                <TableCell
                  align='center'
                  sx={{ whiteSpace: 'nowrap' }}>
                  {t('saas')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <InstanceItemRow
                  key={row.id}
                  item={row}
                />
              ))}
              <EmptyRows
                numberOfCurrentRows={list.length}
                height={40}
              />
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default InstancesList;
